"use client"

/* eslint-disable functional/immutable-data -- The canvas and ripple list are owned by this effect and released on unmount. */
import { useEffect, useRef } from "react"

import { BRICK_COLORS, shade } from "./brick-font"
import styles from "./Top.module.css"

const STUD = "#e9e6d9"
const STUD_EDGE = "#dcd8c6"
const STUD_LIGHT = "#fbfaf5"
const REDUCED_MOTION = "(prefers-reduced-motion: reduce)"
/** 波紋の広がる速さ（px/ms）と寿命（ms） */
const SPEED = 0.55
const LIFE = 1500

interface Ripple {
  x: number
  y: number
  born: number
  strength: number
}

/**
 * ヒーローの地を、上から見たブロックの土台（スタッドの並び）にする。
 * マウスの移動・タップ・オープニングの着地で、水面のようにスタッドが波打つ。
 * 波紋がないあいだは描画しない。
 */
export function HeroStuds() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const hero = canvas?.closest<HTMLElement>("[data-atelier-hero]")
    const ctx = canvas?.getContext("2d")
    if (!canvas || !hero || !ctx) return
    const reduced = window.matchMedia(REDUCED_MOTION)
    let width = 1
    let height = 1
    let gap = 28
    let radius = 5
    let ripples: Ripple[] = []
    let frame = 0
    let visible = true
    let lastX = -999
    let lastY = -999

    const drawStud = (x: number, y: number, lift: number) => {
      if (lift > 0.03) {
        const color = shade(BRICK_COLORS.yellow, -0.12 * (1 - lift))
        ctx.globalAlpha = Math.min(1, 0.2 + lift * 0.9)
        ctx.fillStyle = shade(BRICK_COLORS.yellow, -0.35)
        ctx.beginPath()
        ctx.arc(x + 1, y + 1.5, radius, 0, Math.PI * 2)
        ctx.fill()
        ctx.fillStyle = color
        ctx.beginPath()
        ctx.arc(x, y - lift * 3, radius * (1 + lift * 0.3), 0, Math.PI * 2)
        ctx.fill()
        ctx.globalAlpha = 1
        return
      }
      ctx.fillStyle = STUD_EDGE
      ctx.beginPath()
      ctx.arc(x + 0.8, y + 1.1, radius, 0, Math.PI * 2)
      ctx.fill()
      ctx.fillStyle = STUD
      ctx.beginPath()
      ctx.arc(x, y, radius, 0, Math.PI * 2)
      ctx.fill()
      ctx.fillStyle = STUD_LIGHT
      ctx.beginPath()
      ctx.arc(x - radius * 0.3, y - radius * 0.3, radius * 0.35, 0, Math.PI * 2)
      ctx.fill()
    }

    const draw = (now: number) => {
      ctx.clearRect(0, 0, width, height)
      ripples = ripples.filter((ripple) => now - ripple.born < LIFE)
      for (let y = gap / 2; y < height; y += gap) {
        for (let x = gap / 2; x < width; x += gap) {
          let lift = 0
          for (const ripple of ripples) {
            const age = now - ripple.born
            const band =
              (Math.hypot(x - ripple.x, y - ripple.y) - age * SPEED) / 34
            lift += Math.exp(-band * band) * ripple.strength * (1 - age / LIFE)
          }
          drawStud(x, y, Math.min(1, lift))
        }
      }
    }

    const loop = (now: number) => {
      frame = 0
      draw(now)
      if (ripples.length > 0 && visible) frame = requestAnimationFrame(loop)
    }
    const kick = () => {
      if (!frame && visible) frame = requestAnimationFrame(loop)
    }
    const addRipple = (clientX: number, clientY: number, strength: number) => {
      if (reduced.matches) return
      const bounds = canvas.getBoundingClientRect()
      ripples.push({
        x: clientX - bounds.left,
        y: clientY - bounds.top,
        born: performance.now(),
        strength,
      })
      if (ripples.length > 8) ripples.shift()
      kick()
    }

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      width = canvas.clientWidth
      height = canvas.clientHeight
      canvas.width = Math.round(width * dpr)
      canvas.height = Math.round(height * dpr)
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      gap = width < 768 ? 22 : 28
      radius = gap * 0.2
      draw(performance.now())
    }
    const onMove = (event: PointerEvent) => {
      if (event.pointerType !== "mouse") return
      if (Math.hypot(event.clientX - lastX, event.clientY - lastY) < 110) return
      lastX = event.clientX
      lastY = event.clientY
      addRipple(event.clientX, event.clientY, 0.45)
    }
    const onDown = (event: PointerEvent) => {
      addRipple(event.clientX, event.clientY, 1)
    }
    const onOpening = (event: Event) => {
      if (!(event instanceof CustomEvent)) return
      const detail: unknown = event.detail
      if (
        typeof detail === "object" &&
        detail !== null &&
        "x" in detail &&
        "y" in detail &&
        typeof detail.x === "number" &&
        typeof detail.y === "number"
      )
        addRipple(detail.x, detail.y, 1)
    }
    const observer = new IntersectionObserver(([entry]) => {
      visible = Boolean(entry?.isIntersecting)
      if (visible && ripples.length > 0) kick()
    })
    const resizeObserver = new ResizeObserver(resize)
    observer.observe(canvas)
    resizeObserver.observe(canvas)
    hero.addEventListener("pointermove", onMove)
    hero.addEventListener("pointerdown", onDown)
    window.addEventListener("yo:stud-ripple", onOpening)
    resize()
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
      resizeObserver.disconnect()
      hero.removeEventListener("pointermove", onMove)
      hero.removeEventListener("pointerdown", onDown)
      window.removeEventListener("yo:stud-ripple", onOpening)
    }
  }, [])

  return <canvas ref={canvasRef} className={styles.studs} aria-hidden="true" />
}
